import React, { useState, useEffect } from 'react';
import EmailLogger from '../../utils/emailLogger';

const EmailDebugPanel = () => {
  const [logs, setLogs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const refreshLogs = () => {
      setLogs(EmailLogger.getLogs());
    };

    refreshLogs();

    let interval;
    if (autoRefresh) {
      interval = setInterval(refreshLogs, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const getLogIcon = (type) => {
    switch (type) {
      case 'trigger': return '📧';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'fallback': return '🔄';
      case 'skip': return 'ℹ️';
      case 'info': return '📧';
      default: return '📧';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'trigger': return '#2563eb';
      case 'success': return '#16a34a';
      case 'error': return '#dc2626';
      case 'fallback': return '#ea580c';
      case 'skip': return '#6b7280';
      case 'info': return '#374151';
      default: return '#374151';
    }
  };

  if (!isVisible) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <button
          onClick={() => setIsVisible(true)}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
          title="Show Email Debug Panel"
        >
          📧
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: '400px',
      maxHeight: '500px',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
      zIndex: 1000,
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontWeight: 'bold', color: '#374151' }}>
          📧 Email Debug Panel ({logs.length})
        </div>
        <div>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            style={{
              backgroundColor: autoRefresh ? '#16a34a' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              marginRight: '8px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            {autoRefresh ? '⏸️' : '▶️'}
          </button>
          <button
            onClick={() => EmailLogger.clearLogs()}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              marginRight: '8px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            🗑️
          </button>
          <button
            onClick={() => EmailLogger.exportLogs()}
            style={{
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              marginRight: '8px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            💾
          </button>
          <button
            onClick={() => setIsVisible(false)}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Logs */}
      <div style={{
        maxHeight: '400px',
        overflowY: 'auto',
        padding: '8px'
      }}>
        {logs.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
            No email events yet. Try posting or replying in the forum.
          </div>
        ) : (
          logs.slice().reverse().map((log, index) => (
            <div
              key={index}
              style={{
                padding: '8px',
                marginBottom: '4px',
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                borderLeft: `3px solid ${getLogColor(log.type)}`
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <span style={{ marginRight: '8px' }}>{getLogIcon(log.type)}</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: getLogColor(log.type),
                  flex: 1
                }}>
                  {log.message}
                </span>
                <span style={{ color: '#6b7280', fontSize: '10px' }}>
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
              </div>
              {Object.keys(log).filter(key => !['timestamp', 'type', 'message'].includes(key)).length > 0 && (
                <details style={{ marginTop: '4px' }}>
                  <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Details</summary>
                  <pre style={{
                    marginTop: '4px',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    fontSize: '10px',
                    overflow: 'auto',
                    maxHeight: '100px'
                  }}>
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(log).filter(([key]) => !['timestamp', 'type', 'message'].includes(key))
                      ),
                      null,
                      2
                    )}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmailDebugPanel;